import { useState, useEffect, useRef, useCallback } from "react";
import { Send, MessageCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";

// Private in-match chat. Polls every 5s. Messages auto-delete after 24h (backend TTL).
export default function MatchChat({ matchId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(true);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getMatchChat(matchId);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      // Silent — 403 if the user left the match
    }
  }, [matchId]);

  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (!document.hidden) load();
    }, 5000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages.length, open]);

  const handleSend = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    setText("");
    try {
      const created = await api.postMatchChat(matchId, msg);
      setMessages((prev) => [...prev, created]);
    } catch (err) {
      toast.error(err.message || "Envoi impossible");
      setText(msg);
    } finally {
      setSending(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
      return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    } catch (_) { return ""; }
  };

  return (
    <section data-testid="match-chat" style={{ padding: "0 16px 24px 16px" }}>
      <div style={{ backgroundColor: "#111", border: "0.5px solid #333", borderRadius: "16px", overflow: "hidden" }}>
        {/* Header */}
        <button
          data-testid="chat-toggle"
          onClick={() => setOpen((o) => !o)}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 8, padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer",
          }}
        >
          <span className="font-heading" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "#fff" }}>
            <MessageCircle size={16} color="#22c55e" /> Discussion
            {messages.length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 600, color: "#22c55e", background: "rgba(34,197,94,0.12)", padding: "1px 8px", borderRadius: 999 }}>
                {messages.length}
              </span>
            )}
          </span>
          <ChevronDown size={18} color="#6b7280" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </button>

        {open && (
          <>
            <div
              data-testid="chat-messages"
              style={{
                borderTop: "0.5px solid #222", padding: "14px 16px",
                maxHeight: "300px", overflowY: "auto",
                display: "flex", flexDirection: "column", gap: "12px",
              }}
            >
              {messages.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <MessageCircle size={22} color="#333" style={{ margin: "0 auto 6px" }} />
                  <p style={{ fontSize: "12px", color: "#6b7280" }}>Lance la discussion avec les joueurs</p>
                </div>
              ) : (
                messages.map((m) => {
                  const mine = m.user_id === user?.id;
                  return (
                    <div
                      key={m.id}
                      data-testid={`chat-msg-${m.id}`}
                      style={{ display: "flex", flexDirection: "column", alignItems: mine ? "flex-end" : "flex-start", gap: 2 }}
                    >
                      {!mine && (
                        <span style={{ fontSize: "10px", color: "#22c55e", fontWeight: 700, paddingLeft: 4 }}>
                          {m.user_name}
                        </span>
                      )}
                      <div style={{
                        maxWidth: "80%", padding: "8px 12px", borderRadius: "14px",
                        fontSize: "13px", lineHeight: 1.4, wordBreak: "break-word",
                        backgroundColor: mine ? "#22c55e" : "#1e1e1e",
                        color: mine ? "#052e13" : "#e5e7eb",
                        fontWeight: mine ? 500 : 400,
                        borderBottomRightRadius: mine ? "4px" : "14px",
                        borderBottomLeftRadius: mine ? "14px" : "4px",
                      }}>
                        {m.message}
                      </div>
                      <span style={{ fontSize: "9px", color: "#4b5563", padding: "0 4px" }}>{fmtTime(m.created_at)}</span>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={handleSend} style={{ display: "flex", gap: "8px", padding: "10px 12px", borderTop: "0.5px solid #222" }}>
              <input
                data-testid="chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={500}
                placeholder="Ton message..."
                style={{
                  flex: 1, height: "42px", borderRadius: "999px", border: "1px solid #333",
                  backgroundColor: "#0a0a0a", color: "#fff", padding: "0 16px", fontSize: "16px", outline: "none",
                }}
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                disabled={sending || !text.trim()}
                aria-label="Envoyer"
                style={{
                  height: "42px", width: "42px", borderRadius: "50%", border: "none",
                  backgroundColor: text.trim() ? "#22c55e" : "#222",
                  color: text.trim() ? "#052e13" : "#6b7280",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: text.trim() ? "pointer" : "not-allowed", flexShrink: 0,
                  transition: "background-color 0.15s",
                }}
              >
                <Send size={17} />
              </button>
            </form>
          </>
        )}
      </div>
      <p style={{ fontSize: "10px", color: "#4b5563", textAlign: "center", marginTop: "8px" }}>
        Visible par les joueurs inscrits · les messages s'effacent après 24h
      </p>
    </section>
  );
}
